import React from 'react'
import {
    Box,
    Flex,
    Text,
    IconButton,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    PopoverArrow,
    PopoverHeader,
    Tag,
    useToast,
} from '@chakra-ui/react'
import { Icon } from '@chakra-ui/icons'
import { FaClock, FaXmark, FaEye } from 'react-icons/fa6'

import {
    ScheduledMessageFieldsFragment,
    useCancelScheduledMessageMutation,
    MessageMethod,
} from '~graphql/generated/graphql'
import { useUserContext } from '~config/user-context'
import { useFormatDate } from '~utils/hooks/use-format-date'
import { DateFormat } from '~utils/shared-types'

interface Props {
    message: ScheduledMessageFieldsFragment
    onCancelled: () => void
}

const METHOD_LABELS: Record<string, string> = {
    [MessageMethod.Sms]: 'SMS',
    [MessageMethod.Email]: 'Email',
    [MessageMethod.Push]: 'Push',
    [MessageMethod.Postcard]: 'Postcard',
    [MessageMethod.PhoneCall]: 'Call',
}

export default function ScheduledMessageRow({ message, onCancelled }: Props) {
    const { user } = useUserContext()
    const toast = useToast()
    const { formatDate } = useFormatDate()

    const [cancelMessage, { loading: cancelling }] =
        useCancelScheduledMessageMutation({
            onCompleted: () => {
                toast({
                    status: 'info',
                    description: 'Scheduled message cancelled.',
                    duration: 3000,
                    position: 'top',
                })
                onCancelled()
            },
            onError: () => {
                toast({
                    status: 'error',
                    description: 'Failed to cancel scheduled message.',
                    duration: 3000,
                    position: 'top',
                })
            },
        })

    function handleCancel() {
        if (!user?.id) return
        cancelMessage({
            variables: {
                scheduledMessageId: message.id,
                cancelledBy: user.id,
            },
        })
    }

    const sendTimeLabel = formatDate(message.send_time, DateFormat.DateAtTime)
    const truncatedText =
        message.text.length > 80
            ? message.text.slice(0, 80) + '...'
            : message.text

    return (
        <Flex
            align="center"
            gap={2}
            bg="brand.50"
            rounded="lg"
            px={3}
            py={1.5}
            borderWidth={1}
            borderColor="brand.100"
        >
            <Icon as={FaClock} color="brand.400" boxSize={3} flexShrink={0} />
            <Box flex={1} minW={0}>
                <Text fontSize="xs" color="gray.700" noOfLines={1}>
                    <Text as="span" fontWeight="semibold">
                        Scheduled
                    </Text>
                    {' for '}
                    <Text as="span" fontWeight="semibold">
                        {sendTimeLabel}
                    </Text>
                    {' — '}
                    {truncatedText}
                </Text>
            </Box>
            <Flex gap={1} flexShrink={0}>
                {message.message_methods.map(m => (
                    <Tag
                        key={m}
                        size="sm"
                        colorScheme="brand"
                        variant="subtle"
                        fontSize="2xs"
                    >
                        {METHOD_LABELS[m] ?? m}
                    </Tag>
                ))}
            </Flex>
            <Popover placement="top" trigger="hover">
                <PopoverTrigger>
                    <IconButton
                        aria-label="Preview message"
                        icon={<Icon as={FaEye} />}
                        size="xs"
                        variant="ghost"
                        color="gray.500"
                        _hover={{ color: 'brand.600' }}
                    />
                </PopoverTrigger>
                <PopoverContent w="340px" rounded="xl">
                    <PopoverArrow />
                    <PopoverHeader
                        fontSize="xs"
                        fontWeight="semibold"
                        border="none"
                        pb={0}
                    >
                        Scheduled for {sendTimeLabel}
                        {message.subject && (
                            <Text
                                fontSize="xs"
                                color="gray.500"
                                fontWeight="normal"
                            >
                                Subject: {message.subject}
                            </Text>
                        )}
                    </PopoverHeader>
                    <PopoverBody>
                        <Text
                            fontSize="sm"
                            whiteSpace="pre-wrap"
                            maxH="200px"
                            overflowY="auto"
                        >
                            {message.email_text || message.text}
                        </Text>
                    </PopoverBody>
                </PopoverContent>
            </Popover>
            <IconButton
                aria-label="Cancel scheduled message"
                icon={<Icon as={FaXmark} />}
                size="xs"
                variant="ghost"
                color="gray.400"
                _hover={{ color: 'red.500' }}
                isLoading={cancelling}
                onClick={handleCancel}
            />
        </Flex>
    )
}
